import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CommerceModuleKey } from '@app/core/commerce/commerce.models';
import { CommerceModulesService } from '@app/core/commerce/commerce-modules.service';
import { buildCommerceMenuSections } from '@app/feature/commerce-ui/commerce-menu';
import { AuthService } from './auth.service';
import { PermissionService } from './permission.service';
import { UserSettingsService } from './user-settings.service';

/**
 * Menu Configuration Service
 * Provides a single source of truth for both sidebar and homepage menu configurations
 * Supports hierarchical structure for sidebar and flat structure for homepage
 */

export interface MenuItemConfig {
  name: string; // Hard English name for checking conditions and route mapping
  label: string; // Translation key
  route?: string;
  disabled?: boolean;
  icon?: string;
  command?: () => void;
  permission?: string; // Deprecated: Use permissionCode instead
  permissionCode?: string; // Menu code for permission check (e.g., "inventory.inventory_lookup.view")
  children?: MenuItemConfig[]; // For nested items (3-level hierarchy)
  items?: MenuItemConfig[]; // For processed items in sidebar format
  // Route mapping fields
  path?: string; // From ROUTE_MAP
  parent?: string; // From ROUTE_MAP
  firstChild?: string; // From ROUTE_MAP
  secondChild?: string | null; // From ROUTE_MAP
  /** When set, item is shown only if BO module flag and commerce read permission match. */
  commerceModule?: CommerceModuleKey;
}

export interface MenuSectionConfig {
  name: string; // Hard English name for checking conditions
  title: string; // Translation key for title
  icon: string;
  root: boolean;
  permission?: string; // Required permission to view this section (READ permission)
  disabled?: boolean; // Disabled if all children are disabled
  items: MenuItemConfig[]; // Flat array of items for this section
}

export interface RouteMapping {
  path: string;
  parent: string;
  firstChild: string | null;
  secondChild: string | null;
}

export interface SidebarMenuItem {
  name: string;
  label: string;
  icon: string;
  root: boolean;
  permission?: string; // Required permission to view this section
  disabled?: boolean; // Disabled if all children are disabled
  items: MenuItemConfig[][]; // PrimeNG MegaMenu format (array of arrays)
}

export interface HomepageSection {
  name: string;
  title: string;
  icon?: string;
  items: Array<{
    name: string; // Unique identifier for tracking
    label: string;
    route?: string;
    hasRoute: boolean;
  }>;
}

@Injectable({
  providedIn: 'root',
})
export class MenuConfigService {
  private readonly translateService = inject(TranslateService);
  private readonly authService = inject(AuthService);
  private readonly permissionService = inject(PermissionService);
  private readonly userSettingsService = inject(UserSettingsService);
  private readonly commerceModules = inject(CommerceModulesService);

  constructor() {
    this.commerceModules.loadModules().subscribe();
  }

  private isCommerceMenuItemVisible(item: MenuItemConfig): boolean {
    if (!item.commerceModule) {
      return true;
    }
    if (!this.commerceModules.isEnabledSync(item.commerceModule)) {
      return false;
    }
    return !item.permissionCode || this.permissionService.canDoAction(item.permissionCode);
  }

  /**
   * Unified menu configuration - single source of truth
   * This configuration combines data from both sidebar.ts initializeMenuItems() and sidebar.config.ts ROUTE_MAP
   * Supports both sidebar hierarchy and homepage flat structure
   */
  private readonly menuConfig: MenuSectionConfig[] = [
    // Channel Management
    {
      name: 'Channel',
      title: 'sidebar.business_center.channel_management',
      icon: 'storefront',
      root: true,
      items: [
        {
          name: 'Online Store',
          label: 'sidebar.business_center.online_store_management',
          route: '/channel/online-store',
          path: '/channel/online-store',
          parent: 'Channel',
          firstChild: 'Online Store',
          secondChild: null,
          permissionCode: 'channel.online_store.view',
        },
        {
          name: 'Retail Store',
          label: 'sidebar.business_center.retail_store_management',
          route: '/channel/retail-store',
          path: '/channel/retail-store',
          parent: 'Channel',
          firstChild: 'Retail Store',
          secondChild: null,
          permissionCode: 'channel.retail_store.view',
        },
        {
          name: 'Company Management',
          label: 'sidebar.setting.company_management',
          route: '/channel/company',
          path: '/channel/company',
          parent: 'Channel',
          firstChild: 'Company Management',
          secondChild: null,
          disabled: false,
          permissionCode: 'channel.company.view',
        },
      ],
    },

    // Point of Sale (POS)
    {
      name: 'POS',
      title: 'sidebar.business_center.point_of_sale',
      icon: 'point_of_sale',
      root: true,
      items: [
        {
          name: 'POS Profile',
          label: 'sidebar.business_center.pos_profile',
          route: '/pos/profile',
          path: '/pos/profile',
          parent: 'POS',
          firstChild: 'POS Profile',
          secondChild: null,
          permissionCode: 'pos.pos_profile.view',
        },
        {
          name: 'Open POS Shift',
          label: 'sidebar.business_center.pos_shift_opening_tracking',
          route: '/pos/shift-open',
          path: '/pos/shift-open',
          parent: 'POS',
          firstChild: 'Open POS Shift',
          secondChild: null,
          permissionCode: 'pos.open_pos_shift.view',
        },
        {
          name: 'Close POS Shift',
          label: 'sidebar.business_center.pos_shift_closing_tracking',
          route: '/pos/shift-close',
          path: '/pos/shift-close',
          parent: 'POS',
          firstChild: 'Close POS Shift',
          secondChild: null,
          permissionCode: 'pos.close_pos_shift.view',
        },
      ],
    },

    // Product Management
    {
      name: 'Product',
      title: 'sidebar.business_center.product_management',
      icon: 'hexagon',
      root: true,
      items: [
        {
          name: 'Product',
          label: 'sidebar.business_center.product_management',
          route: '/goods/spu',
          path: '/goods/spu',
          parent: 'Product',
          firstChild: 'Product',
          secondChild: null,
          permissionCode: 'product.product.view',
        },
        {
          name: 'SKU',
          label: 'sidebar.business_center.sku_management',
          route: '/goods/sku/ordinary',
          path: '/goods/sku',
          parent: 'Product',
          firstChild: 'SKU',
          secondChild: null,
          permissionCode: 'product.sku.view',
        },
        {
          name: 'Category',
          label: 'sidebar.business_center.category_management',
          route: '/goods/classify',
          path: '/goods/classify',
          parent: 'Product',
          firstChild: 'Category',
          secondChild: null,
          permissionCode: 'product.category.view',
        },
        {
          name: 'Brand',
          label: 'sidebar.business_center.brand_management',
          route: '/goods/brand',
          path: '/goods/brand',
          parent: 'Product',
          firstChild: 'Brand',
          secondChild: null,
          permissionCode: 'product.brand.view',
        },
        {
          name: 'Product Asset',
          label: 'sidebar.business_center.product_asset_management',
          route: '/goods/material',
          path: '/goods/material',
          parent: 'Product',
          firstChild: 'Product Asset',
          secondChild: null,
          permissionCode: 'product.product_asset.view',
        },
        {
          name: 'Product Review & Approval',
          label: 'sidebar.business_center.product_review_approval',
          route: '/goods/audit/sku/ordinary',
          path: '/goods/audit/sku/ordinary',
          parent: 'Product',
          firstChild: 'Product Review & Approval',
          secondChild: null,
          disabled: true,
          permissionCode: 'product.product_review_approval.view',
        },
        {
          name: 'Sync Error Report',
          label: 'sidebar.business_center.sync_error_report',
          route: '/goods/sync-report',
          path: '/goods/sync-report',
          parent: 'Product',
          firstChild: 'Sync Error Report',
          secondChild: null,
          permissionCode: 'product.sync_report.view',
        },
      ],
    },

    {
      name: 'Price book',
      title: 'sidebar.business_center.price_book',
      icon: 'sell',
      root: true,
      items: [
        {
          name: 'Product Pricing',
          label: 'sidebar.business_center.product_pricing',
          route: '/goods/product-pricing',
          path: '/goods/product-pricing',
          parent: 'Price book',
          firstChild: 'Product Pricing',
          secondChild: null,
          disabled: false,
          permissionCode: 'product.product_pricing.view',
        },
        {
          name: 'Price change history',
          label: 'sidebar.business_center.price_history',
          route: '/goods/price-history',
          path: '/goods/price-history',
          parent: 'Price book',
          firstChild: 'Price change history',
          secondChild: null,
          disabled: false,
          permissionCode: 'product.price_history.view',
        },
      ],
    },

    // Inventory Management
    {
      name: 'Inventory',
      title: 'sidebar.business_center.inventory_management',
      icon: 'inventory_2',
      root: true,
      items: [
        {
          name: 'Inventory Lookup',
          label: 'sidebar.business_center.inventory_lookup',
          route: '/stock/search',
          path: '/stock/search',
          parent: 'Inventory',
          firstChild: 'Inventory Lookup',
          secondChild: null,
          permissionCode: 'inventory.inventory_lookup.view',
        },
        {
          name: 'Inventory Change Lookup',
          label: 'sidebar.business_center.inventory_change_lookup',
          route: '/stock/change',
          path: '/stock/change',
          parent: 'Inventory',
          firstChild: 'Inventory Change Lookup',
          secondChild: null,
          disabled: false,
          permissionCode: 'inventory.inventory_change_lookup.view',
        },
        {
          name: 'Barcode Correction Log',
          label: 'sidebar.business_center.barcode_correction_log',
          route: '/stock/barcode-corrections',
          path: '/stock/barcode-corrections',
          parent: 'Inventory',
          firstChild: 'Barcode Correction Log',
          secondChild: null,
          disabled: false,
          permissionCode: 'inventory.barcode_correction_log.view',
        },
        {
          name: 'Inbound',
          label: 'sidebar.business_center.inbound_management',
          route: '/stock/inbound',
          path: '/stock/inbound',
          parent: 'Inventory',
          firstChild: 'Inbound',
          secondChild: null,
          permissionCode: 'inventory.inbound.view',
        },
        {
          name: 'Outbound',
          label: 'sidebar.business_center.outbound_management',
          route: '/stock/delivery',
          path: '/stock/delivery',
          parent: 'Inventory',
          firstChild: 'Outbound',
          secondChild: null,
          disabled: false,
          permissionCode: 'inventory.outbound.view',
        },
        {
          name: 'Order Printing & Shipping',
          label: 'sidebar.business_center.order_printing_shipping',
          route: '/stock/order-printing-shipping',
          path: '/stock/printing-shipping',
          parent: 'Inventory',
          firstChild: 'Order Printing & Shipping',
          secondChild: null,
          disabled: true,
          permissionCode: 'inventory.order_printing_shipping.view',
        },
        {
          name: 'Warehouse Transfer',
          label: 'sidebar.business_center.warehouse_transfer',
          route: '/stock/transfer-order',
          path: '/stock/transfer-order',
          parent: 'Inventory',
          firstChild: 'Warehouse Transfer',
          secondChild: null,
          disabled: false,
          permissionCode: 'inventory.warehouse_transfer.view',
        },
        {
          name: 'Reserved Inventory',
          label: 'sidebar.business_center.reserved_inventory_management',
          route: '/stock/reserved-inventory',
          path: '/stock/reserved-inventory',
          parent: 'Inventory',
          firstChild: 'Reserved Inventory',
          secondChild: null,
          disabled: true,
          permissionCode: 'inventory.reserved_inventory.view',
        },
        {
          name: 'Batch',
          label: 'sidebar.business_center.batch_management',
          route: '/stock/batch',
          path: '/stock/batch',
          parent: 'Inventory',
          firstChild: 'Batch',
          secondChild: null,
          permissionCode: 'inventory.batch.view',
        },
        {
          name: 'Lot Trace',
          label: 'sidebar.business_center.lot_trace',
          route: '/stock/lot-trace',
          path: '/stock/lot-trace',
          parent: 'Inventory',
          firstChild: 'Lot Trace',
          secondChild: null,
          permissionCode: 'inventory.lot_trace.view',
        },
        {
          name: 'FEFO Violation',
          label: 'sidebar.business_center.fefo_violation',
          route: '/stock/fefo-violation',
          path: '/stock/fefo-violation',
          parent: 'Inventory',
          firstChild: 'FEFO Violation',
          secondChild: null,
          permissionCode: 'inventory.fefo_violation.view',
        },
        {
          name: 'FEFO Compliance',
          label: 'sidebar.business_center.fefo_compliance',
          route: '/stock/fefo-compliance',
          path: '/stock/fefo-compliance',
          parent: 'Inventory',
          firstChild: 'FEFO Compliance',
          secondChild: null,
          permissionCode: 'inventory.fefo_compliance.view',
        },
        {
          name: 'Stock Alert',
          label: 'sidebar.business_center.stock_alert',
          route: '/stock/alert',
          path: '/stock/alert',
          parent: 'Inventory',
          firstChild: 'Stock Alert',
          secondChild: null,
          permissionCode: 'inventory.stock_alert.view',
        },
        {
          name: 'Inventory Change Approval',
          label: 'sidebar.business_center.inventory_change_approval',
          route: '/stock/change-approval',
          path: '/stock/change-approval',
          parent: 'Inventory',
          firstChild: 'Inventory Change Approval',
          secondChild: null,
          disabled: true,
          permissionCode: 'inventory.inventory_change_approval.view',
        },
        {
          name: 'Virtual Warehouse Allocation',
          label: 'sidebar.business_center.virtual_warehouse_allocation',
          route: '/stock/virtual-warehouse-allocation',
          path: '/stock/virtual-warehouse-allocation',
          parent: 'Inventory',
          firstChild: 'Virtual Warehouse Allocation',
          secondChild: null,
          disabled: true,
          permissionCode: 'inventory.virtual_warehouse_allocation.view',
        },
        {
          name: 'Opening Inventory',
          label: 'sidebar.business_center.initial_inventory_adjustment',
          route: '/stock/initial-inventory-adjustment',
          path: '/stock/initial-inventory-adjustment',
          parent: 'Inventory',
          firstChild: 'Opening Inventory',
          secondChild: null,
          disabled: true,
          permissionCode: 'inventory.opening_inventory.view',
        },
        {
          name: 'Inventory Adjustment',
          label: 'sidebar.business_center.stock_synchronization',
          route: '/stock/synchronization',
          path: '/stock/synchronization',
          parent: 'Inventory',
          firstChild: 'Inventory Adjustment',
          secondChild: null,
          permissionCode: 'inventory.inventory_adjustment.view',
        },
        {
          name: 'Pack / Unpack',
          label: 'sidebar.business_center.package_sku_operations',
          route: '/stock/package-operation',
          path: '/stock/package-operation',
          parent: 'Inventory',
          firstChild: 'Pack / Unpack',
          secondChild: null,
          permissionCode: 'inventory.package_sku_operations.view',
        },
      ],
    },

    // Procurement Management
    {
      name: 'Procurement',
      title: 'sidebar.business_center.procurement_management',
      icon: 'shopping_cart',
      root: true,
      items: [
        {
          name: 'Purchase Request',
          label: 'sidebar.business_center.purchase_request_management',
          route: '/procurement/purchase-request',
          path: '/procurement/purchase-request',
          parent: 'Procurement',
          firstChild: 'Purchase Request',
          secondChild: null,
          permissionCode: 'procurement.purchase_request.view',
        },
        {
          name: 'Purchase Order',
          label: 'sidebar.business_center.purchase_order_management',
          route: '/procurement/purchase-order',
          path: '/procurement/purchase-order',
          parent: 'Procurement',
          firstChild: 'Purchase Order',
          secondChild: null,
          permissionCode: 'procurement.purchase_order.view',
        },
        {
          name: 'Vendor',
          label: 'sidebar.business_center.supplier_management',
          route: '/procurement/vendor',
          path: '/procurement/vendor',
          parent: 'Procurement',
          firstChild: 'Vendor',
          secondChild: null,
          permissionCode: 'procurement.vendor.view',
        },
        {
          name: 'Vendor Products',
          label: 'sidebar.business_center.supplier_products',
          route: '/procurement/supplier-products',
          path: '/procurement/supplier-products',
          parent: 'Procurement',
          firstChild: 'Vendor Products',
          secondChild: null,
          disabled: true,
          permissionCode: 'procurement.vendor_products.view',
        },
        {
          name: 'Purchase Inbound Order',
          label: 'sidebar.business_center.purchase_inbound_order',
          route: '/procurement/purchase-inbound-order',
          path: '/procurement/purchase-inbound-order',
          parent: 'Procurement',
          firstChild: 'Purchase Inbound Order',
          secondChild: null,
          disabled: true,
          permissionCode: 'procurement.purchase_inbound_order.view',
        },
        {
          name: 'Purchase Return Order',
          label: 'sidebar.business_center.purchase_return_order',
          route: '/procurement/purchase-return-order',
          path: '/procurement/purchase-return-order',
          parent: 'Procurement',
          firstChild: 'Purchase Return Order',
          secondChild: null,
          disabled: false,
          permissionCode: 'procurement.purchase_return_order.view',
        },
      ],
    },

    // Distribution (dealer) Management
    {
      name: 'Distribution',
      title: 'sidebar.business_center.distribution_management',
      icon: 'storefront',
      root: true,
      items: [
        {
          name: 'Sales Request',
          label: 'sidebar.business_center.sales_request_management',
          route: '/distribution/sales-request',
          path: '/distribution/sales-request',
          parent: 'Distribution',
          firstChild: 'Sales Request',
          secondChild: null,
          permissionCode: 'distribution.sales_request.view',
        },
        {
          name: 'Sales Order',
          label: 'sidebar.business_center.sales_order_management',
          route: '/distribution/sales-order',
          path: '/distribution/sales-order',
          parent: 'Distribution',
          firstChild: 'Sales Order',
          secondChild: null,
          permissionCode: 'distribution.sales_order.view',
        },
      ],
    },

    // Third-Party Management
    {
      name: 'Third-Party',
      title: 'sidebar.business_center.third_party_management',
      icon: 'local_mall',
      root: true,
      items: [
        {
          name: 'Third-Party Products',
          label: 'sidebar.business_center.third_party_products',
          route: '/third/goods',
          path: '/third/goods',
          parent: 'Third-Party',
          firstChild: 'Third-Party Products',
          secondChild: null,
          disabled: false,
          permissionCode: 'third-party.third-party_products.view',
        },
        {
          name: 'Third-Party Sales Orders',
          label: 'sidebar.business_center.third_party_sales_orders',
          route: '/third/order',
          path: '/third/order',
          parent: 'Third-Party',
          firstChild: 'Third-Party Sales Orders',
          secondChild: null,
          disabled: false,
          permissionCode: 'third-party.third-party_sales_orders.view',
        },
        {
          name: 'Third-Party O2O Orders',
          label: 'sidebar.business_center.third_party_o2o_orders',
          route: '/third/o2o',
          path: '/third/o2o',
          parent: 'Third-Party',
          firstChild: 'Third-Party O2O Orders',
          secondChild: null,
          disabled: true,
          permissionCode: 'third-party.third-party_o2o_orders.view',
        },
        {
          name: 'Third-Party After-Sales Orders',
          label: 'sidebar.business_center.third_party_after_sales_orders',
          route: '/third/after',
          path: '/third/after',
          parent: 'Third-Party',
          firstChild: 'Third-Party After-Sales Orders',
          secondChild: null,
          disabled: true,
          permissionCode: 'third-party.third-party_after-sales_orders.view',
        },
      ],
    },

    // Orders Management
    {
      name: 'Orders',
      title: 'sidebar.business_center.standard_orders',
      icon: 'receipt_long',
      root: true,
      items: [
        {
          name: 'Standard Sales Orders',
          label: 'sidebar.business_center.internal_sales_orders',
          route: '/order/internal',
          path: '/order/internal',
          parent: 'Orders',
          firstChild: 'Standard Sales Orders',
          secondChild: null,
          permissionCode: 'orders.standard_sales_orders.view',
        },
        {
          name: 'Pending Sales Orders',
          label: 'sidebar.business_center.pending_sales_orders',
          route: '/order/audit',
          path: '/order/audit',
          parent: 'Orders',
          firstChild: 'Pending Sales Orders',
          secondChild: null,
          disabled: true,
          permissionCode: 'orders.pending_sales_orders.view',
        },
        {
          name: 'Pre-allocation',
          label: 'sidebar.business_center.pre_allocation_management',
          route: '/order/preempt',
          path: '/order/preempt',
          parent: 'Orders',
          firstChild: 'Pre-allocation',
          secondChild: null,
          disabled: true,
          permissionCode: 'orders.pre-allocation.view',
        },
        {
          name: 'Sourcing',
          label: 'sidebar.business_center.sourcing_management',
          route: '/order/source',
          path: '/order/source',
          parent: 'Orders',
          firstChild: 'Sourcing',
          secondChild: null,
          disabled: true,
          permissionCode: 'orders.sourcing.view',
        },
        {
          name: 'Order Stockout',
          label: 'sidebar.business_center.order_stockout_management',
          route: '/order/short',
          path: '/order/short',
          parent: 'Orders',
          firstChild: 'Order Stockout',
          secondChild: null,
          disabled: true,
          permissionCode: 'orders.order_stockout.view',
        },
      ],
    },

    // O2O Orders
    {
      name: 'O2O Orders',
      title: 'sidebar.business_center.o2o_orders',
      icon: 'description',
      root: true,
      items: [
        {
          name: 'O2O Orders',
          label: 'sidebar.business_center.internal_o2o_orders',
          route: '/o2o/internal',
          path: '/o2o/internal',
          parent: 'O2O Orders',
          firstChild: 'O2O Orders',
          secondChild: null,
          permissionCode: 'o2o_orders.o2o_orders.view',
        },
        {
          name: 'Pending O2O Orders',
          label: 'sidebar.business_center.pending_o2o_orders',
          route: '/o2o/audit',
          path: '/o2o/audit',
          parent: 'O2O Orders',
          firstChild: 'Pending O2O Orders',
          secondChild: null,
          disabled: true,
          permissionCode: 'o2o_orders.pending_o2o_orders.view',
        },
        {
          name: 'O2O Change History',
          label: 'sidebar.business_center.o2o_change_history',
          route: '/o2o/change-history',
          path: '/o2o/change-history',
          parent: 'O2O Orders',
          firstChild: 'O2O Change History',
          secondChild: null,
          permissionCode: 'o2o_orders.order_change_history.view',
        },
        {
          name: 'Stuck Stock-Hold Alerts',
          label: 'sidebar.business_center.stuck_stock_hold_alerts',
          route: '/o2o/stuck-alerts',
          path: '/o2o/stuck-alerts',
          parent: 'O2O Orders',
          firstChild: 'Stuck Stock-Hold Alerts',
          secondChild: null,
          permissionCode: 'o2o_orders.stuck_alerts.view',
        },
      ],
    },

    // Warehouse Management
    {
      name: 'Warehouse',
      title: 'sidebar.business_center.warehouse_management',
      icon: 'warehouse',
      root: true,
      items: [
        {
          name: 'Logical Warehouse',
          label: 'sidebar.business_center.logical_warehouse_management',
          route: '/warehouse/logical',
          path: '/warehouse/logical',
          parent: 'Warehouse',
          firstChild: 'Logical Warehouse',
          secondChild: null,
          permissionCode: 'warehouse.logical_warehouse.view',
        },
        {
          name: 'Virtual Warehouse',
          label: 'sidebar.business_center.virtual_warehouse_management',
          route: '/warehouse/virtual',
          path: '/warehouse/virtual',
          parent: 'Warehouse',
          firstChild: 'Virtual Warehouse',
          secondChild: null,
          permissionCode: 'warehouse.virtual_warehouse.view',
        },
        {
          name: 'Virtual Warehouse Page',
          label: 'sidebar.business_center.virtual_warehouse_page',
          route: '/warehouse/virtual-page',
          path: '/warehouse/virtual-page',
          parent: 'Warehouse',
          firstChild: 'Virtual Warehouse Page',
          secondChild: null,
          permissionCode: 'warehouse.virtual.view',
        },
      ],
    },

    // E-Invoice Management
    {
      name: 'E-Invoice',
      title: 'sidebar.e_invoice.title',
      icon: 'receipt_long',
      root: true,
      items: [
        {
          name: 'E-Invoice History',
          label: 'sidebar.e_invoice.history',
          route: '/e-invoice/history',
          path: '/e-invoice/history',
          parent: 'E-Invoice',
          firstChild: 'E-Invoice History',
          secondChild: null,
          permissionCode: 'e-invoice.e_invoice_history.view',
        },
      ],
    },

    // After-Sales Management
    {
      name: 'After-Sales',
      title: 'sidebar.business_center.after_sales_management',
      icon: 'headset_mic',
      root: true,
      items: [
        {
          name: 'After-Sales Orders',
          label: 'sidebar.business_center.internal_after_sales_orders',
          route: '/after/internal/return',
          path: '/after/internal/return',
          parent: 'After-Sales',
          firstChild: 'After-Sales Orders',
          secondChild: null,
          permissionCode: 'after-sales.after-sales_orders.view',
        },
        {
          name: 'Return Exception Approval',
          label: 'sidebar.business_center.return_exception',
          route: '/after/return-exception',
          path: '/after/return-exception',
          parent: 'After-Sales',
          firstChild: 'Return Exception Approval',
          secondChild: null,
          permissionCode: 'after.return_exception.view',
        },
        {
          name: 'Pending After-Sales Orders',
          label: 'sidebar.business_center.pending_after_sales_orders',
          route: '/after/audit/return',
          path: '/after/audit/return',
          parent: 'After-Sales',
          firstChild: 'Pending After-Sales Orders',
          secondChild: null,
          disabled: true,
          permissionCode: 'after-sales.pending_after-sales_orders.view',
        },
      ],
    },

    // Marketing Management
    {
      name: 'Marketing',
      title: 'sidebar.business_center.marketing_management',
      icon: 'campaign',
      root: true,
      items: [
        {
          name: 'Promotion',
          label: 'sidebar.business_center.promotion_management',
          route: '/marketing/promotion',
          path: '/marketing/promotion',
          parent: 'Marketing',
          firstChild: 'Promotion',
          secondChild: null,
          permissionCode: 'marketing.promotion.view',
        },
        // {
        //   name: 'Gift Campaign',
        //   label: 'sidebar.business_center.gift_campaign_management',
        //   route: '/marketing/gift-campaign',
        //   path: '/marketing/gift-campaign',
        //   parent: 'Marketing',
        //   firstChild: 'Gift Campaign',
        //   secondChild: null,
        //   disabled: true,
        // },
        // {
        //   name: 'Bundled Item Rules',
        //   label: 'sidebar.business_center.bundled_item_rules_management',
        //   route: '/marketing/bundled-item-rules',
        //   path: '/marketing/bundled-item-rules',
        //   parent: 'Marketing',
        //   firstChild: 'Bundled Item Rules',
        //   secondChild: null,
        //   disabled: true,
        // },
        {
          name: 'Flash Sale',
          label: 'sidebar.business_center.flash_sale_management',
          route: '/marketing/flash-sale',
          path: '/marketing/flash-sale',
          parent: 'Marketing',
          firstChild: 'Flash Sale',
          secondChild: null,
          permissionCode: 'marketing.flash_sale.view',
        },
        {
          name: 'Coupon',
          label: 'sidebar.business_center.coupon_management',
          route: '/marketing/coupon',
          path: '/marketing/coupon',
          parent: 'Marketing',
          firstChild: 'Coupon',
          secondChild: null,
          permissionCode: 'marketing.coupon.view',
        },
      ],
    },

    // Finance Management
    {
      name: 'Finance',
      title: 'sidebar.business_center.finance_management',
      icon: 'account_balance',
      root: true,
      disabled: true,
      items: [
        {
          name: 'Payment History',
          label: 'sidebar.business_center.payment_history',
          route: '/finance/payment-history',
          path: '/finance/payment-history',
          parent: 'Finance',
          firstChild: 'Payment History',
          secondChild: null,
          disabled: true,
          permissionCode: 'finance.payment_history.view',
        },
        {
          name: 'Marketplace Bills (Raw)',
          label: 'sidebar.business_center.platform_bill_original',
          route: '/finance/marketplace-bills',
          path: '/finance/marketplace-bills',
          parent: 'Finance',
          firstChild: 'Marketplace Bills (Raw)',
          secondChild: null,
          disabled: true,
          permissionCode: 'finance.marketplace_bills_raw.view',
        },
        {
          name: 'Marketplace Billing',
          label: 'sidebar.business_center.platform_bill_management',
          route: '/finance/marketplace-billing',
          path: '/finance/marketplace-billing',
          parent: 'Finance',
          firstChild: 'Marketplace Billing',
          secondChild: null,
          disabled: true,
          permissionCode: 'finance.marketplace_billing.view',
        },
        {
          name: 'Marketplace Reconciliation',
          label: 'sidebar.business_center.platform_reconciliation_management',
          route: '/finance/marketplace-reconciliation',
          path: '/finance/marketplace-reconciliation',
          parent: 'Finance',
          firstChild: 'Marketplace Reconciliation',
          secondChild: null,
          disabled: true,
          permissionCode: 'finance.marketplace_reconciliation.view',
        },
      ],
    },

    // Report Management
    {
      name: 'Report',
      title: 'sidebar.business_center.report_management',
      icon: 'assessment',
      root: true,
      disabled: true,
      items: [
        {
          name: 'Channel Reports',
          label: 'sidebar.business_center.channel_reports',
          route: '/report/channel-report',
          path: '/report/channel-report',
          parent: 'Report',
          firstChild: 'Channel Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.channel_reports.view',
        },
        {
          name: 'Point of Sale Reports',
          label: 'sidebar.business_center.pos_reports',
          route: '/report/pos-report',
          path: '/report/pos-report',
          parent: 'Report',
          firstChild: 'Point of Sale Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.point_of_sale_reports.view',
        },
        {
          name: 'Product Reports',
          label: 'sidebar.business_center.product_reports',
          route: '/report/product-report',
          path: '/report/product-report',
          parent: 'Report',
          firstChild: 'Product Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.product_reports.view',
        },
        {
          name: 'Inventory Reports',
          label: 'sidebar.business_center.inventory_reports',
          route: '/report/inventory-report',
          path: '/report/inventory-report',
          parent: 'Report',
          firstChild: 'Inventory Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.inventory_reports.view',
        },
        {
          name: 'Procurement Reports',
          label: 'sidebar.business_center.procurement_reports',
          route: '/report/procurement-report',
          path: '/report/procurement-report',
          parent: 'Report',
          firstChild: 'Procurement Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.procurement_reports.view',
        },
        {
          name: 'Third-Party Reports',
          label: 'sidebar.business_center.third_party_reports',
          route: '/report/third-party-report',
          path: '/report/third-party-report',
          parent: 'Report',
          firstChild: 'Third-Party Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.third-party_reports.view',
        },
        {
          name: 'Orders Reports',
          label: 'sidebar.business_center.orders_reports',
          route: '/report/orders-report',
          path: '/report/orders-report',
          parent: 'Report',
          firstChild: 'Orders Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.orders_reports.view',
        },
        {
          name: 'O2O Orders Reports',
          label: 'sidebar.business_center.o2o_orders_reports',
          route: '/report/o2o-orders-report',
          path: '/report/o2o-orders-report',
          parent: 'Report',
          firstChild: 'O2O Orders Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.o2o_orders_reports.view',
        },
        {
          name: 'Warehouse Reports',
          label: 'sidebar.business_center.warehouse_reports',
          route: '/report/warehouse-report',
          path: '/report/warehouse-report',
          parent: 'Report',
          firstChild: 'Warehouse Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.warehouse_reports.view',
        },
        {
          name: 'After-Sales Reports',
          label: 'sidebar.business_center.after_sales_reports',
          route: '/report/after-sales-report',
          path: '/report/after-sales-report',
          parent: 'Report',
          firstChild: 'After-Sales Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.after-sales_reports.view',
        },
        {
          name: 'Marketing Reports',
          label: 'sidebar.business_center.marketing_reports',
          route: '/report/marketing-report',
          path: '/report/marketing-report',
          parent: 'Report',
          firstChild: 'Marketing Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.marketing_reports.view',
        },
        {
          name: 'Finance Reports',
          label: 'sidebar.business_center.finance_reports',
          route: '/report/finance-report',
          path: '/report/finance-report',
          parent: 'Report',
          firstChild: 'Finance Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.finance_reports.view',
        },
        {
          name: 'Sales Reports',
          label: 'sidebar.business_center.sales_reports',
          route: '/report/sale-report',
          path: '/report/sale-report',
          parent: 'Report',
          firstChild: 'Sales Reports',
          secondChild: null,
          disabled: true,
          permissionCode: 'report.sales_reports.view',
        },
      ],
    },

    // Commerce: Figma sidebar groups (Customers & Loyalty, Members, People, Workflow, Platform)
    ...buildCommerceMenuSections(),

    // Settings (3-level hierarchy)
    {
      name: 'Settings',
      title: 'sidebar.navigation.setting',
      icon: 'settings',
      root: true,
      items: [
        {
          name: 'User',
          label: 'sidebar.setting.user',
          disabled: false,
          children: [
            {
              name: 'Employee Management',
              label: 'sidebar.setting.employee_management',
              route: '/configuration/user/employee',
              path: '/configuration/user/employee',
              parent: 'Settings',
              firstChild: 'User',
              secondChild: 'Employee Management',
              disabled: false,
              permissionCode: 'settings.user.employee_management.view',
            },
            {
              name: 'Roles and Permissions Management',
              label: 'sidebar.setting.role_permission_management',
              route: '/configuration/user/roles',
              path: '/configuration/user/roles',
              parent: 'Settings',
              firstChild: 'User',
              secondChild: 'Roles and Permissions Management',
              disabled: false,
              permissionCode:
                'settings.user.roles_and_permissions_management.view',
            },
            {
              name: 'Job Role Mapping',
              label: 'sidebar.setting.job_role_mapping',
              route: '/configuration/user/job-role-mapping',
              path: '/configuration/user/job-role-mapping',
              parent: 'Settings',
              firstChild: 'User',
              secondChild: 'Job Role Mapping',
              disabled: false,
              permissionCode:
                'settings.user.roles_and_permissions_management.view',
            },
            {
              name: 'AI Agents API Keys',
              label: 'sidebar.setting.ai_agent_keys',
              route: '/configuration/user/ai-agent-keys',
              path: '/configuration/user/ai-agent-keys',
              parent: 'Settings',
              firstChild: 'User',
              secondChild: 'AI Agents API Keys',
              disabled: false,
              permissionCode: 'settings.user.ai_agent_keys.view',
            },
            {
              name: 'Business Unit Management',
              label: 'sidebar.setting.business_unit_management',
              route: '/configuration/user/business-unit',
              path: '/configuration/user/business-unit',
              parent: 'Settings',
              firstChild: 'User',
              secondChild: 'Business Unit Management',
              disabled: false,
              permissionCode: 'settings.user.business_unit.view',
            },
          ],
        },
        {
          name: 'Price',
          label: 'sidebar.setting.price',
          disabled: false,
          children: [
            {
              name: 'Selling Price Tabs',
              label: 'sidebar.setting.selling_price_tabs',
              route: '/configuration/price/selling-tabs',
              path: '/configuration/price/selling-tabs',
              parent: 'Settings',
              firstChild: 'Price',
              secondChild: 'Selling Price Tabs',
              disabled: false,
              permissionCode: 'settings.price.selling_tabs.view',
            },
            {
              name: 'Flash Sale Quota',
              label: 'sidebar.setting.flash_sale_quota',
              route: '/configuration/price/flash-sale-quota',
              path: '/configuration/price/flash-sale-quota',
              parent: 'Settings',
              firstChild: 'Price',
              secondChild: 'Flash Sale Quota',
              disabled: false,
              permissionCode: 'settings.price.flash_sale_quota.view',
            },
          ],
        },
        {
          name: 'Product',
          label: 'sidebar.setting.product',
          disabled: false,
          children: [
            {
              name: 'Product Approval Policy',
              label: 'sidebar.setting.product_approval_policy',
              route: '/configuration/product-approval',
              path: '/configuration/product-approval',
              parent: 'Settings',
              firstChild: 'Product',
              secondChild: 'Product Approval Policy',
              disabled: true,
              permissionCode: 'settings.product.product_approval_policy.view',
            },
            {
              name: 'Serial Profile',
              label: 'sidebar.setting.serial_profile',
              route: '/configuration/product/serial-profile',
              path: '/configuration/product/serial-profile',
              parent: 'Settings',
              firstChild: 'Product',
              secondChild: 'Serial Profile',
              disabled: false,
              permissionCode: 'settings.product.serial_profile.view',
            },
            {
              name: 'SKU Barcode',
              label: 'sidebar.setting.sku_barcode',
              route: '/configuration/product/sku-barcode',
              path: '/configuration/product/sku-barcode',
              parent: 'Settings',
              firstChild: 'Product',
              secondChild: 'SKU Barcode',
              disabled: false,
              permissionCode: 'settings.product.sku_barcode.view',
            },
            {
              name: 'Lot Barcode',
              label: 'sidebar.setting.lot_barcode',
              route: '/configuration/product/lot-barcode',
              path: '/configuration/product/lot-barcode',
              parent: 'Settings',
              firstChild: 'Product',
              secondChild: 'Lot Barcode',
              disabled: false,
              permissionCode: 'settings.product.lot_barcode.view',
            },
            {
              name: 'Tax Categories',
              label: 'sidebar.setting.tax_categories',
              route: '/configuration/product/tax-categories',
              path: '/configuration/product/tax-categories',
              parent: 'Settings',
              firstChild: 'Product',
              secondChild: 'Tax Categories',
              disabled: false,
              permissionCode: 'settings.product.tax_categories.view',
            },
          ],
        },
        {
          name: 'Inventory',
          label: 'sidebar.setting.inventory',
          disabled: false,
          children: [
            {
              name: 'Warehouse Structure Configuration',
              label: 'sidebar.setting.warehouse_structure_configuration',
              route: '/configuration/warehouse-structure',
              path: '/configuration/warehouse-structure',
              parent: 'Settings',
              firstChild: 'Inventory',
              secondChild: 'Warehouse Structure Configuration',
              disabled: true,
              permissionCode:
                'settings.inventory.warehouse_structure_configuration.view',
            },
            {
              name: 'Inventory Ratio Configuration',
              label: 'sidebar.setting.inventory_ratio_configuration',
              route: '/configuration/inventory-ratio',
              path: '/configuration/inventory-ratio',
              parent: 'Settings',
              firstChild: 'Inventory',
              secondChild: 'Inventory Ratio Configuration',
              disabled: true,
              permissionCode:
                'settings.inventory.inventory_ratio_configuration.view',
            },
            {
              name: 'Inventory Approval Policy',
              label: 'sidebar.setting.inventory_approval_policy',
              route: '/configuration/inventory-approval',
              path: '/configuration/inventory-approval',
              parent: 'Settings',
              firstChild: 'Inventory',
              secondChild: 'Inventory Approval Policy',
              disabled: true,
              permissionCode:
                'settings.inventory.inventory_approval_policy.view',
            },
            {
              name: 'Virtual Warehouse Stock Rules',
              label: 'sidebar.setting.virtual_warehouse_stock_rules',
              route: '/configuration/virtual-warehouse-rules',
              path: '/configuration/virtual-warehouse-rules',
              parent: 'Settings',
              firstChild: 'Inventory',
              secondChild: 'Virtual Warehouse Stock Rules',
              disabled: true,
              permissionCode:
                'settings.inventory.virtual_warehouse_stock_rules.view',
            },
            {
              name: 'Alert Settings',
              label: 'sidebar.setting.alert_settings',
              route: '/configuration/inventory/alert-settings',
              path: '/configuration/inventory/alert-settings',
              parent: 'Settings',
              firstChild: 'Inventory',
              secondChild: 'Alert Settings',
              disabled: false,
              permissionCode: 'settings.inventory.alert_settings.view',
            },
            {
              name: 'Stock Hold Timeout',
              label: 'sidebar.setting.stock_hold_timeout',
              route: '/configuration/inventory/stock-hold-timeout',
              path: '/configuration/inventory/stock-hold-timeout',
              parent: 'Settings',
              firstChild: 'Inventory',
              secondChild: 'Stock Hold Timeout',
              disabled: false,
              permissionCode: 'settings.inventory.stock_hold_timeout.view',
            },
            {
              name: 'Return Expiry Threshold',
              label: 'sidebar.setting.return_expiry_threshold',
              route: '/configuration/inventory/return-expiry-threshold',
              path: '/configuration/inventory/return-expiry-threshold',
              parent: 'Settings',
              firstChild: 'Inventory',
              secondChild: 'Return Expiry Threshold',
              disabled: false,
              permissionCode: 'settings.inventory.return_expiry_threshold.view',
            },
          ],
        },
        {
          name: 'Logistics',
          label: 'sidebar.setting.logistics',
          children: [
            {
              name: 'Carrier Management',
              label: 'sidebar.setting.carrier_management',
              route: '/configuration/carrier',
              path: '/configuration/carrier',
              parent: 'Settings',
              firstChild: 'Logistics',
              secondChild: 'Carrier Management',
              permissionCode: 'settings.logistics.carrier_management.view',
            },
            {
              name: 'Logistics Policy',
              label: 'sidebar.setting.logistics_policy',
              route: '/configuration/logistics-policy',
              path: '/configuration/logistics-policy',
              parent: 'Settings',
              firstChild: 'Logistics',
              secondChild: 'Logistics Policy',
              disabled: true,
              permissionCode: 'settings.logistics.logistics_policy.view',
            },
            {
              name: 'Logistics Authorization',
              label: 'sidebar.setting.logistics_authorization',
              route: '/configuration/logistics-authorization',
              path: '/configuration/logistics-authorization',
              parent: 'Settings',
              firstChild: 'Logistics',
              secondChild: 'Logistics Authorization',
              disabled: true,
              permissionCode: 'settings.logistics.logistics_authorization.view',
            },
          ],
        },
        {
          name: 'Procurement',
          label: 'sidebar.setting.procurement',
          disabled: false,
          children: [
            {
              name: 'Purchase Order Configuration',
              label: 'sidebar.setting.purchase_order_configuration',
              route: '/configuration/procurement/purchase-order-config',
              path: '/configuration/procurement/purchase-order-config',
              parent: 'Settings',
              firstChild: 'Procurement',
              secondChild: 'Purchase Order Configuration',
              disabled: false,
              permissionCode:
                'settings.procurement.purchase_order_configuration.view',
            },
          ],
        },
        {
          name: 'Order',
          label: 'sidebar.setting.order',
          disabled: false,
          children: [
            {
              name: 'Sourcing Configuration',
              label: 'sidebar.setting.sourcing_configuration',
              route: '/configuration/order/sourcing-config',
              path: '/configuration/order/sourcing-config',
              parent: 'Settings',
              firstChild: 'Order',
              secondChild: 'Sourcing Configuration',
              disabled: true,
              permissionCode: 'settings.order.sourcing_configuration.view',
            },
            {
              name: 'Sourcing Policy',
              label: 'sidebar.setting.sourcing_policy',
              route: '/configuration/order/sourcing-policy',
              path: '/configuration/order/sourcing-policy',
              parent: 'Settings',
              firstChild: 'Order',
              secondChild: 'Sourcing Policy',
              disabled: true,
              permissionCode: 'settings.order.sourcing_policy.view',
            },
            {
              name: 'System Order Labels',
              label: 'sidebar.setting.system_order_labels',
              route: '/configuration/order/system-order-labels',
              path: '/configuration/order/system-order-labels',
              parent: 'Settings',
              firstChild: 'Order',
              secondChild: 'System Order Labels',
              disabled: true,
              permissionCode: 'settings.order.system_order_labels.view',
            },
            {
              name: 'Manual Order Labels',
              label: 'sidebar.setting.manual_order_labels',
              route: '/configuration/order/manual-order-labels',
              path: '/configuration/order/manual-order-labels',
              parent: 'Settings',
              firstChild: 'Order',
              secondChild: 'Manual Order Labels',
              disabled: true,
              permissionCode: 'settings.order.manual_order_labels.view',
            },
            {
              name: 'After Sales Reasons',
              label: 'sidebar.setting.after_sales_reasons',
              route: '/configuration/order/after-sales-reasons',
              path: '/configuration/order/after-sales-reasons',
              parent: 'Settings',
              firstChild: 'Order',
              secondChild: 'After Sales Reasons',
              disabled: true,
              permissionCode: 'settings.order.after_sales_reasons.view',
            },
            {
              name: 'Delivery Retry',
              label: 'sidebar.setting.delivery_retry',
              route: '/configuration/order/delivery-retry',
              path: '/configuration/order/delivery-retry',
              parent: 'Settings',
              firstChild: 'Order',
              secondChild: 'Delivery Retry',
              disabled: false,
              permissionCode: 'settings.order.delivery_retry.view',
            },
            {
              name: 'Modification Approval Timeout',
              label: 'sidebar.setting.modification_approval_timeout',
              route: '/configuration/order/modification-approval-timeout',
              path: '/configuration/order/modification-approval-timeout',
              parent: 'Settings',
              firstChild: 'Order',
              secondChild: 'Modification Approval Timeout',
              disabled: false,
              permissionCode:
                'settings.order.modification_approval_timeout.view',
            },
            {
              name: 'Modification Approval Threshold',
              label: 'sidebar.setting.modification_approval_threshold',
              route: '/configuration/order/modification-approval-threshold',
              path: '/configuration/order/modification-approval-threshold',
              parent: 'Settings',
              firstChild: 'Order',
              secondChild: 'Modification Approval Threshold',
              disabled: false,
              permissionCode:
                'settings.order.modification_approval_threshold.view',
            },
            {
              name: 'Cash Payment Max Amount',
              label: 'sidebar.setting.cash_payment_max_amount',
              route: '/configuration/order/cash-payment-max-amount',
              path: '/configuration/order/cash-payment-max-amount',
              parent: 'Settings',
              firstChild: 'Order',
              secondChild: 'Cash Payment Max Amount',
              disabled: false,
              permissionCode: 'settings.order.cash_payment_max_amount.view',
            },

          ],
        },
        {
          name: 'Finance',
          label: 'sidebar.setting.finance',
          disabled: true,
          children: [
            {
              name: 'Payment Configuration',
              label: 'sidebar.setting.payment_configuration',
              route: '/configuration/finance/payment-configuration',
              path: '/configuration/finance/payment-configuration',
              parent: 'Settings',
              firstChild: 'Finance',
              secondChild: 'Payment Configuration',
              disabled: true,
              permissionCode: 'settings.finance.payment_configuration.view',
            },
            {
              name: 'Platform Billing Parsing Policy',
              label: 'sidebar.setting.platform_billing_parsing_policy',
              route: '/configuration/finance/billing-parsing',
              path: '/configuration/finance/billing-parsing',
              parent: 'Settings',
              firstChild: 'Finance',
              secondChild: 'Platform Billing Parsing Policy',
              disabled: true,
              permissionCode:
                'settings.finance.platform_billing_parsing_policy.view',
            },
          ],
        },
        {
          name: 'E-Invoice',
          label: 'sidebar.setting.e_invoice',
          disabled: false,
          children: [
            {
              name: 'Provider Management',
              label: 'sidebar.setting.e_invoice_provider',
              route: '/configuration/e-invoice/provider',
              path: '/configuration/e-invoice/provider',
              parent: 'Settings',
              firstChild: 'E-Invoice',
              secondChild: 'Provider Management',
              disabled: false,
              // Permission code placeholder until backend permissions are finalized.
              permissionCode: 'settings.e_invoice_provider.view',
            },
            {
              name: 'Account Management',
              label: 'sidebar.setting.e_invoice_account',
              route: '/configuration/e-invoice/account',
              path: '/configuration/e-invoice/account',
              parent: 'Settings',
              firstChild: 'E-Invoice',
              secondChild: 'Account Management',
              disabled: false,
              // Permission code placeholder until backend permissions are finalized.
              permissionCode: 'settings.e_invoice_account.view',
            },
          ],
        },
        {
          name: 'Master Data',
          label: 'sidebar.setting.master_data',
          children: [
            {
              name: 'Transaction List',
              label: 'sidebar.setting.transaction_list',
              route: '/configuration/transaction-list',
              path: '/configuration/transaction-list',
              parent: 'Settings',
              firstChild: 'Master Data',
              secondChild: 'Transaction List',
              disabled: true,
              permissionCode: 'settings.master_data.transaction_list.view',
            },
            {
              name: 'Workflow Design',
              label: 'sidebar.setting.workflow_design',
              route: '/configuration/master-data/bpmn',
              path: '/configuration/master-data/bpmn',
              parent: 'Settings',
              firstChild: 'Master Data',
              secondChild: 'Workflow Design',
              permissionCode: 'settings.master_data.workflow_design.view',
            },
            {
              name: 'Workflow Engine Settings',
              label: 'sidebar.setting.workflow_engine_settings',
              route: '/configuration/master-data/workflow-engine',
              path: '/configuration/master-data/workflow-engine',
              parent: 'Settings',
              firstChild: 'Master Data',
              secondChild: 'Workflow Engine Settings',
              permissionCode: 'settings.master_data.workflow_design.view',
            },
            {
              name: 'System Applications',
              label: 'sidebar.setting.system_applications',
              route: '/configuration/system-applications',
              path: '/configuration/system-applications',
              parent: 'Settings',
              firstChild: 'Master Data',
              secondChild: 'System Applications',
              disabled: true,
              permissionCode: 'settings.master_data.system_applications.view',
            },
            {
              name: 'Coding Rules',
              label: 'sidebar.setting.coding_rules',
              route: '/configuration/coding-rules',
              path: '/configuration/coding-rules',
              parent: 'Settings',
              firstChild: 'Master Data',
              secondChild: 'Coding Rules',
              disabled: true,
              permissionCode: 'settings.master_data.coding_rules.view',
            },
            {
              name: 'Value Set Configuration',
              label: 'sidebar.setting.value_set_configuration',
              route: '/configuration/value-set-config',
              path: '/configuration/value-set-config',
              parent: 'Settings',
              firstChild: 'Master Data',
              secondChild: 'Value Set Configuration',
              disabled: true,
              permissionCode:
                'settings.master_data.value_set_configuration.view',
            },
            {
              name: 'Value Set View',
              label: 'sidebar.setting.value_set_view',
              route: '/configuration/value-set-view',
              path: '/configuration/value-set-view',
              parent: 'Settings',
              firstChild: 'Master Data',
              secondChild: 'Value Set View',
              disabled: true,
              permissionCode: 'settings.master_data.value_set_view.view',
            },
            {
              name: 'Label Management',
              label: 'sidebar.setting.label_management',
              route: '/configuration/master-data/tag-management',
              path: '/configuration/master-data/tag-management',
              parent: 'Settings',
              firstChild: 'Master Data',
              secondChild: 'Label Management',
              disabled: true,
              permissionCode: 'settings.master_data.label_management.view',
            },
            {
              name: 'External Store Mapping',
              label: 'sidebar.setting.external_store_mapping',
              route: '/configuration/external-store-mapping',
              path: '/configuration/external-store-mapping',
              parent: 'Settings',
              firstChild: 'Master Data',
              secondChild: 'External Store Mapping',
              disabled: true,
              permissionCode:
                'settings.master_data.external_store_mapping.view',
            },
          ],
        },
        {
          name: 'Import',
          label: 'sidebar.setting.import',
          disabled: true,
          children: [
            {
              name: 'Import History',
              label: 'sidebar.setting.import_history',
              route: '/configuration/import/history',
              path: '/configuration/import/history',
              parent: 'Settings',
              firstChild: 'Import',
              secondChild: 'Import History',
              disabled: true,
              permissionCode: 'settings.import.import_history.view',
            },
            {
              name: 'General Import Management',
              label: 'sidebar.setting.general_import_management',
              route: '/configuration/import/management',
              path: '/configuration/import/management',
              parent: 'Settings',
              firstChild: 'Import',
              secondChild: 'General Import Management',
              disabled: true,
              permissionCode: 'settings.import.general_import_management.view',
            },
          ],
        },
        {
          name: 'File',
          label: 'sidebar.setting.file',
          disabled: true,
          children: [
            {
              name: 'Summary Query',
              label: 'sidebar.setting.summary_query',
              route: '/configuration/file/summary-query',
              path: '/configuration/file/summary-query',
              parent: 'Settings',
              firstChild: 'File',
              secondChild: 'Summary Query',
              disabled: true,
              permissionCode: 'settings.file.summary_query.view',
            },
            {
              name: 'Storage Configuration',
              label: 'sidebar.setting.storage_configuration',
              route: '/configuration/file/storage-config',
              path: '/configuration/file/storage-config',
              parent: 'Settings',
              firstChild: 'File',
              secondChild: 'Storage Configuration',
              disabled: true,
              permissionCode: 'settings.file.storage_configuration.view',
            },
            {
              name: 'Upload Capacity Configuration',
              label: 'sidebar.setting.upload_capacity_configuration',
              route: '/configuration/file/upload-capacity',
              path: '/configuration/file/upload-capacity',
              parent: 'Settings',
              firstChild: 'File',
              secondChild: 'Upload Capacity Configuration',
              disabled: true,
              permissionCode:
                'settings.file.upload_capacity_configuration.view',
            },
            {
              name: 'Upload Detail Configuration',
              label: 'sidebar.setting.upload_detail_configuration',
              route: '/configuration/upload-detail-config',
              path: '/configuration/upload-detail-config',
              parent: 'Settings',
              firstChild: 'File',
              secondChild: 'Upload Detail Configuration',
              permissionCode: 'settings.file.upload_detail_configuration.view',
            },
          ],
        },
      ],
    },
  ];

  /**
   * Check if all items in a section are disabled
   * Considers both direct items and nested children (for 3-level hierarchy)
   */
  private areAllItemsDisabled(section: MenuSectionConfig): boolean {
    if (section.items.length === 0) {
      return true;
    }

    return section.items.every((item) => {
      // If item has children (3-level hierarchy like Settings)
      if (item.children && item.children.length > 0) {
        // Item is considered disabled if all its children are disabled
        // (The parent item's disabled flag is less relevant since it's just a container)
        return item.children.every((child) => child.disabled ?? false);
      }
      // For 2-level items, check if the item is disabled
      return item.disabled ?? false;
    });
  }

  /**
   * Get menu config with disabled flags set on sections
   * A section is disabled if all its children (and nested children) are disabled
   */
  private getProcessedMenuConfig(): MenuSectionConfig[] {
    return this.menuConfig.map((section) => ({
      ...section,
      disabled: this.areAllItemsDisabled(section),
    }));
  }

  /**
   * Hide disabled (unavailable) items unless the user opted in via
   * User Settings to see them grayed out.
   */
  private hideUnavailable(
    sections: MenuSectionConfig[],
  ): MenuSectionConfig[] {
    if (this.userSettingsService.showUnavailableMenuItems()) {
      return sections;
    }
    return sections
      .map((section) => ({
        ...section,
        items: this.getAvailableItems(section.items),
      }))
      .filter((section) => section.items.length > 0);
  }

  /**
   * Drop disabled items and parent groups left without a route or children.
   */
  private getAvailableItems(items: MenuItemConfig[]): MenuItemConfig[] {
    return items
      .filter((item) => !item.disabled && this.isCommerceMenuItemVisible(item))
      .map((item) => this.withAvailableChildren(item))
      .filter((item) => !!item.route || !!item.children?.length);
  }

  private withAvailableChildren(item: MenuItemConfig): MenuItemConfig {
    if (!item.children) {
      return item;
    }
    return {
      ...item,
      children: item.children.filter(
        (child) => !child.disabled && this.isCommerceMenuItemVisible(child),
      ),
    };
  }

  /**
   * Generate route mapping from unified configuration
   * This replaces the need for separate sidebar.config.ts
   */
  getRouteMapping(): RouteMapping[] {
    const routeMap: RouteMapping[] = [];

    this.menuConfig.forEach((section) => {
      section.items.forEach((item) => {
        const itemPath = item.path || item.route;
        if (itemPath) {
          // 2-level route (parent -> firstChild)
          routeMap.push({
            path: itemPath,
            parent: section.name,
            firstChild: item.name,
            secondChild: null,
          });
        }

        // Handle 3-level routes (parent -> firstChild -> secondChild)
        if (item.children) {
          item.children.forEach((child) => {
            const childPath = child.path || child.route;
            if (childPath) {
              routeMap.push({
                path: childPath,
                parent: section.name,
                firstChild: item.name,
                secondChild: child.name,
              });
            }
          });
        }
      });
    });

    return routeMap;
  }

  /**
   * Get sidebar menu configuration in PrimeNG MegaMenu format
   * Returns raw configuration without commands - commands will be added by sidebar component
   */
  getSidebarMenuConfig(): SidebarMenuItem[] {
    // Check if user has admin role
    // const hasAdminRole =
    //   this.authService.hasRole('Administrator') ||
    //   this.authService.hasRole('Admin');

    const hasAdminRole = true; // Temporary hardcoded role for testing.

    const processedConfig = this.hideUnavailable(
      this.getProcessedMenuConfig(),
    );

    return processedConfig.map((section) => ({
      name: section.name,
      label: section.title,
      icon: section.icon,
      root: section.root,
      disabled: section.disabled, // Include disabled flag
      items: [
        section.items.map((item) => {
          const menuItem: any = {
            name: item.name,
            label: item.label,
            disabled: item.disabled,
            route: item.route, // Include route for sidebar component to use
            permissionCode: item.permissionCode,
          };

          // Handle 3-level hierarchy (Settings)
          if (item.children && item.children.length > 0) {
            menuItem.items = item.children.map((child) => {
              // Disable "Upload Detail Configuration" if user doesn't have admin role
              let isDisabled = child.disabled;
              if (
                child.name === 'Upload Detail Configuration' &&
                !hasAdminRole
              ) {
                isDisabled = true;
              }

              return {
                name: child.name,
                label: child.label,
                disabled: isDisabled,
                route: child.route, // Include route for sidebar component to use
                permissionCode: child.permissionCode, // Include permissionCode for permission checks
              };
            });
          }

          return menuItem;
        }),
      ],
    }));
  }

  /**
   * Check if user has permission to view a menu section
   * Uses menuCode-based permission system
   * @param section The menu section configuration
   * @returns true if user has view permission for the section
   */
  private hasMenuPermission(section: {
    permission?: string;
    name?: string;
  }): boolean {
    // Wait for permissions to be loaded before showing menus
    if (!this.permissionService.arePermissionsLoaded()) {
      // If permissions not loaded yet, don't show menus
      // The menu will refresh when permissions are loaded via subscription
      return false;
    }

    // Use menu name-based permission check
    if (section.name) {
      // canViewMenu will handle menu name normalization internally
      // It uses mapMenuNameToModule to ensure consistent mapping
      // (e.g., "Third-Party" -> "third-party")
      // canViewMenu checks if user has view permission for any submenu in this menu
      // Only menus defined in JSON config with matching menuCodes will be shown
      const hasPermission = this.permissionService.canViewMenu(section.name);
      return hasPermission;
    }

    // If no menu name, don't show (except Settings which is handled specially)
    // Settings menu items are checked individually by their routes
    const result = section.name === 'Settings' || section.name === 'settings';
    return result;
  }

  /**
   * Get homepage menu configuration in flat format
   * Filters sections and items based on user permissions
   * Uses the same permission logic as sidebar
   */
  getHomepageMenuConfig(): HomepageSection[] {
    // Wait for permissions to be loaded
    if (!this.permissionService.arePermissionsLoaded()) {
      return [];
    }

    return this.hideUnavailable(this.menuConfig)
      .filter((section) => {
        // Exclude Settings from homepage
        if (section.name === 'Settings') {
          return false;
        }
        // Filter sections based on permissions (same logic as sidebar)
        const hasPermission = this.hasMenuPermission(section);

        return hasPermission;
      })
      .map((section) => {
        return {
          name: section.name,
          icon: section.icon,
          title: section.title,
          // Only include items that user has permission to access
          items: section.items
            .filter((item) => {
              // Check if item has valid route permission (regardless of disabled state)
              const hasPermission =
                (!!item.route &&
                  (item.permissionCode
                    ? this.permissionService.canDoAction(item.permissionCode)
                    : this.permissionService.canAccessRoute(item.route))) ||
                (item.children?.some(
                  (child) =>
                    !!child.route &&
                    (child.permissionCode
                      ? this.permissionService.canDoAction(child.permissionCode)
                      : this.permissionService.canAccessRoute(child.route)),
                ) ??
                  false);
              return hasPermission;
            })
            .map((item) => ({
              name: item.name,
              label: item.label,
              route: item.route,
              disabled: item.disabled, // Include disabled state so UI can gray out
              hasRoute: !!item.route && !item.disabled, // Only clickable if has route and not disabled
            })),
        };
      })
      .filter((section) => {
        const hasItems = section.items.length > 0;
        return hasItems;
      }); // Only include sections with at least one accessible item
  }

  /**
   * Get enabled items only for homepage
   */
  getEnabledHomepageMenuConfig(): HomepageSection[] {
    return this.menuConfig
      .filter((section) => section.name !== 'Settings') // Exclude Settings from homepage
      .map((section) => ({
        name: section.name,
        title: section.title,
        icon: section.icon,
        items: section.items
          .filter((item) => !item.disabled)
          .map((item) => ({
            name: item.name, // Include unique name for tracking
            label: item.label,
            route: item.route,
            hasRoute: true,
          })),
      }));
  }

  /**
   * Get raw configuration for advanced usage
   * Returns processed config with disabled flags set on sections
   */
  getRawConfig(): MenuSectionConfig[] {
    return this.getProcessedMenuConfig();
  }

  /**
   * Find menu item by route
   */
  findMenuItemByRoute(route: string): {
    section: MenuSectionConfig;
    item: MenuItemConfig;
    child?: MenuItemConfig;
  } | null {
    for (const section of this.menuConfig) {
      const result = this.findItemInSection(section, route);
      if (result) {
        return { section, ...result };
      }
    }
    return null;
  }

  private findItemInSection(
    section: MenuSectionConfig,
    route: string,
  ): { item: MenuItemConfig; child?: MenuItemConfig } | null {
    for (const item of section.items) {
      if (item.route === route) {
        return { item };
      }
      const child = this.findChildByRoute(item, route);
      if (child) {
        return { item, child };
      }
    }
    return null;
  }

  private findChildByRoute(
    item: MenuItemConfig,
    route: string,
  ): MenuItemConfig | null {
    if (!item.children) return null;
    return item.children.find((child) => child.route === route) || null;
  }

  /**
   * Get all routes for a specific section
   */
  getSectionRoutes(sectionName: string): string[] {
    const section = this.menuConfig.find((s) => s.name === sectionName);
    if (!section) return [];

    const routes: string[] = [];
    section.items.forEach((item) => {
      if (item.route) routes.push(item.route);
      if (item.children) {
        item.children.forEach((child) => {
          if (child.route) routes.push(child.route);
        });
      }
    });
    return routes;
  }
}
